import { defineStore } from 'pinia';
import { useHeadGenerate } from '~/composables/useHeadGenerate';
import { parseItemInCollection } from '~/utils/strapiComponents/addStrapiData';
import { useStoreRoot } from '../abstract/root';
import { useStoreCountry } from '../client/country';
import { strapi } from '../transport/strapi';
import { useStoreCataloguePage } from './cataloguePage';
import type { IProviderData, IProviderStrapiResponse } from '~/types/providers';

export const parseProvider = (itemData: IProviderData, page: boolean = false): IProviderData => {
  const item: IProviderData = parseItemInCollection(JSON.parse(JSON.stringify(itemData)));

  item.isPage = page;

  if (!item.position) {
    item.position = 99;
  }

  const geo = useStoreRoot().geo;
  const { getMirrorCanonical } = useHeadGenerate();

  if (geo && geo !== 'main') {
    const mirrorHead = item.mirrorHead?.find((head) => head?.geo === geo);

    if (mirrorHead) {
      item.descriptionHiddenText = '';
      item.descriptionText = '';
      item.head.title = mirrorHead.title;
      item.head.description = mirrorHead.description || '';
      item.head.nofollow = mirrorHead.nofollow || false;
      item.head.noindex = mirrorHead.noindex || false;
      item.head.canonical = getMirrorCanonical(mirrorHead);

      if (mirrorHead.noindex || mirrorHead.nofollow) {
        item.head.alternates = [];
      }
    }

    const mirrorBody = item.mirrorBody?.find((body) => body?.geo === geo);

    if (mirrorBody) {
      item.descriptionText = mirrorBody.descriptionText;
    }
  }

  return item;
};

export const useStoreProviders = defineStore('providers', {
  state: () => ({
    data: [] as IProviderData[],
    requestPromise: null as Promise<IProviderStrapiResponse> | null,
  }),

  getters: {
    list(state) {
      return state.data;
    },
    iconsMap(state): { [key: string]: { url: string; height: number; width: number } } {
      return state.data.reduce((acc, el) => ({ ...acc, [el.providerId]: el.providerIcon }), {});
    },
    activeList(state): IProviderData[] {
      const storeRoot = useStoreRoot();
      const storeCountry = useStoreCountry();
      const findPositionByGeo = (provider: IProviderData) =>
        provider.positionByGeo?.find((position) => position?.geo === storeCountry.country)
          ?.position || provider.position;

      return (
        state.data
          // get providers with selected language
          .filter((provider) => provider.locale === storeRoot.userLang)
          .sort((a, b) => (findPositionByGeo(a) || 0) - (findPositionByGeo(b) || 0))
      );
    },
    providerPages(): IProviderData[] {
      return this.data.filter((provider) => provider.isPage);
    },
    sortedProviders(): IProviderData[] {
      return [...this.activeList].sort((a, b) => a.providerId.localeCompare(b.providerId));
    },
    activeProvider(state): IProviderData | null | undefined {
      const storeCataloguePage = useStoreCataloguePage();
      if (!storeCataloguePage.selectedProvider) {
        return null;
      }
      return state.data.find(
        (provider) => provider.providerId === storeCataloguePage.selectedProvider
      );
    },
    no2GamesModeProviderIds() {
      const ids: string[] = [];

      this.list.forEach((provider) => {
        if (provider.no2GamesMode) {
          ids.push(provider.providerId);
        }
      });

      return ids;
    },
    no4GamesModeProviderIds() {
      const ids: string[] = [];

      this.list.forEach((provider) => {
        if (provider.no4GamesMode) {
          ids.push(provider.providerId);
        }
      });

      return ids;
    },
  },

  actions: {
    async loadProviders() {
      if (!this.requestPromise) {
        this.requestPromise = strapi()
          .get('providers-short')
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        this.ADD_DATA_FROM_STRAPI(response.data);

        return true;
      } else {
        return false;
      }
    },
    ADD_DATA_FROM_STRAPI(data: IProviderData[]) {
      const storeRoot = useStoreRoot();
      // Added filter to prevent rewriting "full" provider data / think how to fix in in future
      const parsedData = data
        .map((item) => (item.providerId ? parseProvider(item) : ({} as IProviderData)))
        .filter(
          (parsedItem) =>
            (storeRoot.isEstonianLicense ? parsedItem.showForEstonia : true) &&
            !this.data.some(
              ({ providerId }) => providerId === parsedItem.providerId && parsedItem.isPage
            )
        );

      const filteredState = this.data.filter(
        (item) => !parsedData.some(({ providerId }) => providerId && providerId === item.providerId)
      );

      this.data = [...filteredState, ...parsedData].sort((a, b) =>
        a.providerId.localeCompare(b.providerId)
      );
    },
    ADD_OR_EDIT_ONE_FROM_STRAPI(page: IProviderData) {
      if (useStoreRoot().isEstonianLicense ? !page.showForEstonia : false) {
        return;
      }

      const parsedProvider = parseProvider(page, true);
      const index = this.data.findIndex((p) => p.providerId === parsedProvider.providerId);
      if (index > -1) {
        this.data.splice(index, 1, parsedProvider);
      } else {
        this.data.push(parsedProvider);
      }
    },
  },
});
